import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

import type { DualPathMutationResult } from '../dualPathMutationResult';

export interface DeleteObligationChangeAttestationTRPCInput {
  ObligationChangeId: string;
}

/**
 * Maps the TRPC mutation response to match the GraphQL mutation structure so
 * consumers can branch on success/error without knowing which path ran.
 */
const mapTrpcResponseToGraphQL = (trpcData: { Id: string }) => {
  return {
    deleteChildObligationChangeAttestation: {
      __typename: 'IdOutput' as const,
      Id: trpcData.Id,
    },
  };
};

export type DeleteObligationChangeAttestationResult = ReturnType<
  typeof mapTrpcResponseToGraphQL
>;
export type DeleteObligationChangeAttestationCompatibilityResult =
  DualPathMutationResult<DeleteObligationChangeAttestationResult>;

export const useDeleteObligationChangeAttestationTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.obligationChangeAttestation.delete.mutationOptions({
      onSuccess: async () => {
        // Legacy Apollo path evicted the coarse `obligation_change` field,
        // so invalidate the whole obligation-change sub-router (the family
        // backing getObligationChangeById).
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.obligationChange.pathKey(),
        });
      },
    }),
  });

  return {
    deleteObligationChangeAttestation: async (
      variables: DeleteObligationChangeAttestationTRPCInput
    ): Promise<DeleteObligationChangeAttestationResult> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
